import React, { useState, useEffect, useMemo } from "react";
import {
  ChevronLeft,
  Calendar,
  ClockFading,
  CloudSun,
  ChevronDown,
  Hourglass,
  Clock,
  Sun,
  Lightbulb,
  ChevronRight,
} from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import {
  useApplyForLeaveMutation,
  useGetLeavesQuery,
  useGetLeaveBalanceQuery,
} from "../../slices/leaveApiSlice";
import { useGetCalendarByAcademyYearQuery } from "../../slices/calendarApiSlice";
import { useSelector } from "react-redux";
import { IoIosCloseCircle } from "react-icons/io";

// Calendar Icon Button Component
const CalendarIconButton = ({ onClick, isOpen }) => (
  <button
    type="button"
    onClick={onClick}
    className="absolute right-3 top-1/2 transform -translate-y-1/2 p-2 hover:bg-gray-100 rounded-lg transition-colors"
  >
    <Calendar
      className={`w-5 h-5 ${isOpen ? "text-blue-600" : "text-gray-400"}`}
    />
  </button>
);

// Compact Calendar Dropdown Component
const CompactCalendarDropdown = ({
  isOpen,
  onClose,
  selectedDate,
  onDateSelect,
  holidays = [],
  disabledDates = [],
  currentYear,
  position = "bottom",
  minDate = null,
  maxDate = null,
}) => {
  const [currentDate, setCurrentDate] = useState(
    new Date(currentYear, new Date().getMonth(), 1),
  );
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  if (!isOpen) return null;

  const getDaysInMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth(), 1).getDay();
  };

  const navigateMonth = (direction) => {
    setCurrentDate((prev) => {
      const newDate = new Date(prev);
      newDate.setMonth(prev.getMonth() + direction);

      // Check if the new date is within current year
      if (newDate.getFullYear() === currentYear) {
        return newDate;
      }
      return prev; // Don't navigate if it would change the year
    });
  };

  const formatDate = (date) => {
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
    });
  };

  const isSameDay = (date1, date2) => {
    return date1.toDateString() === date2.toDateString();
  };

  const isWeekend = (date) => {
    const day = date.getDay();
    return day === 0 || day === 6;
  };

  const getHolidaysForDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const dateStr = `${year}-${month}-${day}`;

    const matchingHolidays = holidays.filter((holiday) => {
      if (!holiday || !holiday.date) return false;

      let holidayDateStr = holiday.date;

      if (typeof holidayDateStr === "string") {
        if (holidayDateStr.includes("T")) {
          holidayDateStr = holidayDateStr.split("T")[0];
        }

        if (holidayDateStr.includes("/")) {
          const parts = holidayDateStr.split("/");
          if (parts.length === 3) {
            const [month, day, year] = parts;
            holidayDateStr = `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
          }
        }
      } else if (holidayDateStr instanceof Date) {
        const year = holidayDateStr.getFullYear();
        const month = String(holidayDateStr.getMonth() + 1).padStart(2, "0");
        const day = String(holidayDateStr.getDate()).padStart(2, "0");
        holidayDateStr = `${year}-${month}-${day}`;
      }

      return holidayDateStr === dateStr;
    });

    return matchingHolidays;
  };

  const isDateDisabled = (date) => {
    // Disable dates before minDate
    if (minDate && date < minDate) {
      return true;
    }

    // Disable dates after maxDate
    if (maxDate && date > maxDate) {
      return true;
    }

    // Disable dates before today
    if (date < today) {
      return true;
    }

    // Disable weekends
    if (isWeekend(date)) {
      return true;
    }

    // Disable dates in future years
    if (date.getFullYear() > currentYear) {
      return true;
    }

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const dateStr = `${year}-${month}-${day}`;

    // Check if it's a holiday
    const isHoliday = holidays.some((holiday) => {
      if (!holiday.date) return false;

      let holidayDate = holiday.date;
      if (holidayDate.includes("T")) {
        holidayDate = holidayDate.split("T")[0];
      }
      return holidayDate === dateStr;
    });

    if (isHoliday) return true;

    // Check if date is in disabled dates (existing leaves)
    const dateISOStr = date.toISOString().split("T")[0];
    const isDisabledDate = disabledDates.some((disabledDate) => {
      const disabledDateObj = new Date(disabledDate);
      disabledDateObj.setHours(0, 0, 0, 0);
      return disabledDateObj.getTime() === date.getTime();
    });

    return isDisabledDate;
  };

  const handleToday = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Only select today if it's in the current year and not disabled
    if (today.getFullYear() === currentYear && !isDateDisabled(today)) {
      onDateSelect(today);
    }
  };

  const renderCalendarGrid = () => {
    const daysInMonth = getDaysInMonth(currentDate);
    const firstDay = getFirstDayOfMonth(currentDate);
    const days = [];

    // Empty cells for previous month days
    for (let i = 0; i < firstDay; i++) {
      days.push(<div key={`empty-${i}`} className="h-8"></div>);
    }

    // Days of current month
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(
        currentDate.getFullYear(),
        currentDate.getMonth(),
        day,
      );
      date.setHours(0, 0, 0, 0);

      const dayHolidays = getHolidaysForDate(date);
      const isSelected = selectedDate && isSameDay(date, selectedDate);
      const isToday = isSameDay(date, today);
      const isDisabled = isDateDisabled(date);
      const hasHolidays = dayHolidays.length > 0;

      days.push(
        <button
          key={`day-${day}`}
          type="button"
          disabled={isDisabled}
          className={`h-8 w-8 text-xs flex items-center justify-center rounded border ${
            isDisabled
              ? "bg-gray-100 text-gray-400 cursor-not-allowed border-gray-200"
              : "cursor-pointer hover:bg-gray-50 border-transparent"
          } ${isSelected ? "bg-blue-500 text-white border-blue-600" : ""} ${
            hasHolidays ? "bg-red-100 text-red-700 border-red-200" : ""
          } ${
            isToday && !isSelected ? "border-2 border-green-500" : ""
          } transition-colors duration-200`}
          onClick={() => {
            if (!isDisabled) {
              onDateSelect(date);
              onClose();
            }
          }}
        >
          <span className={`${isToday ? "font-bold" : ""}`}>{day}</span>
        </button>,
      );
    }

    return days;
  };

  // Day headers
  const dayHeaders = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

  // Check if previous month navigation should be disabled
  const isPrevDisabled = currentDate.getMonth() === 0; // January
  // Check if next month navigation should be disabled
  const isNextDisabled = currentDate.getMonth() === 11; // December

  return (
    <div
      className={`absolute ${position === "top" ? "bottom-full mb-2" : "top-full mt-2"} left-0 z-50 bg-white rounded-lg shadow-xl border border-gray-200 w-80`}
    >
      {/* Calendar Header */}
      <div className="bg-zinc-50 border-b border-zinc-200 rounded-t-lg p-3">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center space-x-2">
            <button
              onClick={() => navigateMonth(-1)}
              disabled={isPrevDisabled}
              className={`p-1 rounded transition-colors ${
                isPrevDisabled
                  ? "text-zinc-300 cursor-not-allowed"
                  : "text-zinc-600 hover:bg-zinc-100"
              }`}
            >
              <ChevronLeft className="w-4 h-4" />
            </button>

            <h3 className="text-sm font-medium text-zinc-800 min-w-[140px] text-center">
              {formatDate(currentDate)}
            </h3>

            <button
              onClick={() => navigateMonth(1)}
              disabled={isNextDisabled}
              className={`p-1 rounded transition-colors ${
                isNextDisabled
                  ? "text-zinc-300 cursor-not-allowed"
                  : "text-zinc-600 hover:bg-zinc-100"
              }`}
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>

          <button
            onClick={onClose}
            className="p-1 hover:bg-zinc-100 rounded transition-colors"
          >
            <IoIosCloseCircle className="w-4 h-4 text-zinc-600" />
          </button>
        </div>

        <div className="flex items-center justify-between">
          <button
            onClick={handleToday}
            disabled={
              isDateDisabled(today) || today.getFullYear() !== currentYear
            }
            className={`text-xs px-3 py-1 rounded transition-colors ${
              isDateDisabled(today) || today.getFullYear() !== currentYear
                ? "bg-gray-400 text-gray-200 cursor-not-allowed"
                : "bg-blue-600 text-white hover:bg-blue-700"
            }`}
          >
            Today
          </button>
        </div>
      </div>

      {/* Days of Week Header */}
      <div className="grid grid-cols-7 gap-1 px-3 py-2 border-b border-gray-200">
        {dayHeaders.map((day, index) => (
          <div
            key={`${day}-${index}`}
            className="text-xs font-medium text-gray-500 text-center"
          >
            {day.charAt(0)}
          </div>
        ))}
      </div>

      {/* Calendar Grid */}
      <div className="grid grid-cols-7 gap-1 p-3">{renderCalendarGrid()}</div>

      {/* Compact Legend */}
      <div className="px-3 py-2 border-t bg-gray-50 rounded-b-lg">
        <div className="flex items-center justify-between text-xs text-gray-600">
          <div className="flex items-center space-x-1">
            <div className="w-2 h-2 bg-red-100 border border-red-300 rounded"></div>
            <span>Holiday</span>
          </div>
          <div className="flex items-center space-x-1">
            <div className="w-2 h-2 bg-gray-100 border border-gray-300 rounded"></div>
            <span>Unavailable</span>
          </div>
          <div className="flex items-center space-x-1">
            <div className="w-2 h-2 border-2 border-green-500 rounded"></div>
            <span>Today</span>
          </div>
        </div>
      </div>
    </div>
  );
};
// Error Overlay Component
const ErrorOverlay = ({ message, onClose, title = "Error" }) => null;

// Custom Radio Button Component
const RadioButton = ({ selected, className = "" }) => (
  <div
    className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${className} ${
      selected ? "border-blue-600 bg-blue-600" : "border-gray-400"
    }`}
  >
    {selected && <div className="w-2 h-2 bg-white rounded-full"></div>}
  </div>
);

// Leave Balance Card Component
const LeaveBalanceCard = ({
  title,
  balance,
  color = "blue",
  isLoading = false,
}) => {
  const colorClasses = {
    blue: {
      text: "text-blue-600",
      bg: "bg-blue-50",
      border: "border-blue-200",
      progress: "bg-blue-500",
    },
    purple: {
      text: "text-purple-600",
      bg: "bg-purple-50",
      border: "border-purple-200",
      progress: "bg-purple-500",
    },
  };

  const currentColor = colorClasses[color];

  if (isLoading) {
    return (
      <div className="bg-[#FAFAFA] rounded-lg p-6 animate-pulse">
        <div className="h-4 bg-gray-300 rounded w-1/2 mb-4"></div>
        <div className="space-y-3">
          <div className="h-3 bg-gray-300 rounded"></div>
          <div className="h-3 bg-gray-300 rounded"></div>
          <div className="h-3 bg-gray-300 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-[#FAFAFA] rounded-lg p-6`}>
      <h3 className="font-semibold text-gray-800 mb-4">{title}</h3>
      <div className="space-y-4">
        <div className="flex justify-between items-center">
          <span className="text-gray-600">Total Entitled:</span>
          <span className="font-medium">{balance.total} days</span>
        </div>

        <div className="flex justify-between items-center">
          <span className="text-gray-600">Used:</span>
          <span className="font-medium text-red-600">
            - {balance.used} days
          </span>
        </div>

        <div className="flex justify-between items-center">
          <span className="text-gray-600">Pending:</span>
          <span className="font-medium text-yellow-600">
            - {balance.pending} days
          </span>
        </div>

        <div className="flex justify-between items-center pt-3 border-t border-gray-200">
          <span className="text-gray-800 font-semibold">Available</span>
          <div className="text-right">
            <div className={`text-sm ${currentColor.text} font-bold`}>
              {balance.available} days
            </div>
          </div>
        </div>

        <div className="mt-3">
          <div className="flex justify-between text-xs text-gray-500 mb-1">
            <span>Available: {balance.available} days</span>
            <span>Pending: {balance.pending} days</span>
            <span>Used: {balance.used} days</span>
          </div>
          <div className="flex h-3 rounded-full overflow-hidden border border-gray-300">
            <div
              className={`${currentColor.progress} transition-all duration-300`}
              style={{ width: `${(balance.available / balance.total) * 100}%` }}
            ></div>
            <div
              className="bg-yellow-400 transition-all duration-300"
              style={{ width: `${(balance.pending / balance.total) * 100}%` }}
            ></div>
            <div
              className="bg-gray-200 transition-all duration-300"
              style={{ width: `${(balance.used / balance.total) * 100}%` }}
            ></div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default function ApplyLeave() {
  const navigate = useNavigate();
  const [applyForLeave, { isLoading: isSubmitting }] =
    useApplyForLeaveMutation();

  // Get user info from Redux store
  const { userInfo } = useSelector((state) => state.auth);

  // Get userId from userInfo
  const userId = userInfo?.userId || userInfo?.id || userInfo?._id || "";

  // Fetch existing leaves (including pending leaves)
  const { data: existingLeavesData = [], refetch: refetchLeaves } =
    useGetLeavesQuery(userId, {
      skip: !userId,
    });

  // Fetch leave balance
  const {
    data: leaveBalanceData,
    isLoading: isLoadingBalance,
    error: balanceError,
    refetch: refetchBalance,
  } = useGetLeaveBalanceQuery(undefined, {
    skip: !userId,
    refetchOnMountOrArgChange: true,
  });

  // Get academy ID for fetching calendars
  const academyId = userInfo?.academyId || userInfo?.academy_id || "";

  const [selectedLeaveType, setSelectedLeaveType] = useState("multiple");
  const [selectedHalfDay, setSelectedHalfDay] = useState("");
  const [showLeaveTypeDropdown, setShowLeaveTypeDropdown] = useState(false);
  const [formData, setFormData] = useState({
    leaveType: "",
    startDate: "",
    endDate: "",
    reason: "",
  });
  const [selectedStartDate, setSelectedStartDate] = useState(null);
  const [selectedEndDate, setSelectedEndDate] = useState(null);
  const [errors, setErrors] = useState({});

  // Calendar dropdown states
  const [showStartCalendar, setShowStartCalendar] = useState(false);
  const [showEndCalendar, setShowEndCalendar] = useState(false);

  // Error overlay state
  // const [showErrorOverlay, setShowErrorOverlay] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  // Get current year
  const currentYear = new Date().getFullYear();

  // Calendar query
  const {
    data: calendarData,
    isLoading: isLoadingCalendars,
    error: calendarsError,
    refetch: refetchCalendars,
  } = useGetCalendarByAcademyYearQuery(
    {
      academy_id: academyId,
      year: currentYear,
    },
    {
      skip: !academyId,
      refetchOnMountOrArgChange: true,
    },
  );

  // Holidays data
  const holidays = useMemo(() => {
    if (!calendarData) {
      return [];
    }

    let holidaysFromAPI = [];

    // Extract holidays from calendarData
    if (calendarData.holidays && Array.isArray(calendarData.holidays)) {
      holidaysFromAPI = calendarData.holidays;
    } else if (calendarData.calendar && calendarData.calendar.holidays) {
      holidaysFromAPI = calendarData.calendar.holidays;
    } else if (Array.isArray(calendarData)) {
      holidaysFromAPI = calendarData;
    }

    // Transform holidays
    const transformedHolidays = holidaysFromAPI
      .map((holiday, index) => {
        if (!holiday) return null;

        // Get date from various possible fields
        const holidayDate = holiday.date || holiday.holidayDate;
        if (!holidayDate) {
          return null;
        }

        // Get name from various possible fields
        const holidayName =
          holiday.title ||
          holiday.name ||
          holiday.holidayName ||
          holiday.description ||
          "Holiday";

        // Normalize date
        let normalizedDate = holidayDate;
        if (typeof holidayDate === "string" && holidayDate.includes("T")) {
          normalizedDate = holidayDate.split("T")[0];
        }

        return {
          id: holiday._id || holiday.id || `holiday-${index}`,
          date: normalizedDate,
          name: holidayName,
          title: holidayName,
        };
      })
      .filter((holiday) => holiday !== null);

    return transformedHolidays;
  }, [calendarData]);

  // Get disabled dates (existing leaves including pending)
  const disabledDates = useMemo(() => {
    if (!existingLeavesData || existingLeavesData.length === 0) return [];

    const dates = [];
    existingLeavesData.forEach((leave) => {
      // Include both APPROVED and PENDING leaves
      if (
        leave.status === "APPROVED" ||
        leave.status === "Approved" ||
        leave.status === "PENDING" ||
        leave.status === "Pending"
      ) {
        const start = new Date(leave.startDate);
        const end = new Date(leave.endDate);
        const current = new Date(start);

        while (current <= end) {
          // Only include dates from current year onwards
          if (current.getFullYear() >= currentYear) {
            const dateCopy = new Date(current);
            dateCopy.setHours(0, 0, 0, 0);
            dates.push(dateCopy);
          }
          current.setDate(current.getDate() + 1);
        }
      }
    });

    return dates;
  }, [existingLeavesData, currentYear]);

  // Calculate days excluding weekends and holidays
  const calculateWorkingDays = (startDate, endDate) => {
    if (!startDate || !endDate) return 0;

    const start = new Date(startDate);
    const end = new Date(endDate);
    let count = 0;

    const current = new Date(start);
    while (current <= end) {
      const day = current.getDay();
      // Skip weekends (Saturday = 6, Sunday = 0)
      if (day !== 0 && day !== 6) {
        // Check if it's a holiday
        const dateStr = current.toISOString().split("T")[0];
        const isHoliday = holidays.some((holiday) => {
          if (!holiday.date) return false;
          let holidayDate = holiday.date;
          if (holidayDate.includes("T")) {
            holidayDate = holidayDate.split("T")[0];
          }
          return holidayDate === dateStr;
        });
        if (!isHoliday) {
          count++;
        }
      }
      current.setDate(current.getDate() + 1);
    }

    return count;
  };

  // Calculate used and pending leave days from all leave records - USING WORKING DAYS
  const calculateLeaveDays = useMemo(() => {
    if (
      !existingLeavesData ||
      !Array.isArray(existingLeavesData) ||
      existingLeavesData.length === 0
    ) {
      return {
        earnedUsed: 0,
        casualUsed: 0,
        earnedPending: 0,
        casualPending: 0,
      };
    }

    let earnedUsed = 0;
    let casualUsed = 0;
    let earnedPending = 0;
    let casualPending = 0;

    existingLeavesData.forEach((leave) => {
      // Use working days calculation for existing leaves
      let days = 0;

      if (leave.duration === "HALF_DAY" || leave.duration === "Half Day") {
        days = 0.5;
      } else if (
        leave.duration === "FULL_DAY" ||
        leave.duration === "Full Day"
      ) {
        days = 1;
      } else {
        days = calculateWorkingDays(leave.startDate, leave.endDate);
      }

      if (leave.status === "APPROVED" || leave.status === "Approved") {
        if (leave.leaveType === "EARN" || leave.leaveType === "earned") {
          earnedUsed += days;
        } else if (
          leave.leaveType === "CASUAL" ||
          leave.leaveType === "casual"
        ) {
          casualUsed += days;
        }
      } else if (leave.status === "PENDING" || leave.status === "Pending") {
        if (leave.leaveType === "EARN" || leave.leaveType === "earned") {
          earnedPending += days;
        } else if (
          leave.leaveType === "CASUAL" ||
          leave.leaveType === "casual"
        ) {
          casualPending += days;
        }
      }
    });

    return {
      earnedUsed,
      casualUsed,
      earnedPending,
      casualPending,
    };
  }, [existingLeavesData]);
  // Process leave balance data from API (considering pending leaves)
  const processLeaveBalance = () => {
    if (!leaveBalanceData) {
      return {
        earned: { total: 0, used: 0, pending: 0, available: 0 },
        casual: { total: 0, used: 0, pending: 0, available: 0 },
      };
    }

    const earnedTotal = leaveBalanceData.total_earned_leave_days || 0;
    const casualTotal = leaveBalanceData.total_casual_leave_days || 0;

    const { earnedUsed, casualUsed, earnedPending, casualPending } =
      calculateLeaveDays;

    // Use the API's remaining balance and adjust for pending leaves
    const earnedRemaining =
      leaveBalanceData.earned_leave_balance_remaining || 0;
    const casualRemaining =
      leaveBalanceData.casual_leave_balance_remaining || 0;

    // Available = API remaining balance - pending leaves
    const earnedAvailable = Math.max(0, earnedRemaining - earnedPending);
    const casualAvailable = Math.max(0, casualRemaining - casualPending);

    return {
      earned: {
        total: Math.max(0, earnedTotal),
        used: Math.max(0, earnedUsed),
        pending: Math.max(0, earnedPending),
        available: Math.max(0, earnedAvailable),
      },
      casual: {
        total: Math.max(0, casualTotal),
        used: Math.max(0, casualUsed),
        pending: Math.max(0, casualPending),
        available: Math.max(0, casualAvailable),
      },
    };
  };
  const leaveBalances = processLeaveBalance();

  // Show error overlay function
  const showError = (message) => {
    toast.error(message);
  };

  // Hide error overlay function
  // const hideError = () => {
  // setShowErrorOverlay(false);
  // setErrorMessage("");
  // };

  const handleBack = () => {
    navigate("/manager/attendance");
  };

  const handleLeaveTypeSelect = (type) => {
    setSelectedLeaveType(type);
    if (type !== "half") {
      setSelectedHalfDay("");
    }
    // Clear errors when changing leave type
    setErrors((prev) => ({
      ...prev,
      leaveType: "",
      halfDay: "",
      startDate: "",
      endDate: "",
    }));
  };

  const handleHalfDaySelect = (period) => {
    setSelectedHalfDay(period);
    // Clear half day error when selecting
    if (errors.halfDay) {
      setErrors((prev) => ({ ...prev, halfDay: "" }));
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear field error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const handleLeaveTypeDropdownSelect = (type) => {
    setFormData((prev) => ({ ...prev, leaveType: type }));
    setShowLeaveTypeDropdown(false);
    // Clear leave type error when selecting
    if (errors.leaveType) {
      setErrors((prev) => ({ ...prev, leaveType: "" }));
    }
  };

  const handleStartDateSelect = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const dateStr = `${year}-${month}-${day}`;

    setFormData((prev) => ({ ...prev, startDate: dateStr }));
    setSelectedStartDate(date);

    // Clear start date error when selecting
    if (errors.startDate) {
      setErrors((prev) => ({ ...prev, startDate: "" }));
    }

    // Clear end date if it's before the new start date
    if (formData.endDate && new Date(formData.endDate) < date) {
      setFormData((prev) => ({ ...prev, endDate: "" }));
      setSelectedEndDate(null);
    }

    setShowStartCalendar(false);
  };

  const handleEndDateSelect = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const dateStr = `${year}-${month}-${day}`;

    setFormData((prev) => ({ ...prev, endDate: dateStr }));
    setSelectedEndDate(date);

    // Clear end date error when selecting
    if (errors.endDate) {
      setErrors((prev) => ({ ...prev, endDate: "" }));
    }

    setShowEndCalendar(false);
  };

  const calculateFormDays = () => {
    if (formData.startDate && formData.endDate) {
      return calculateWorkingDays(formData.startDate, formData.endDate);
    }
    return 0;
  };

  // Map frontend leave types to backend enum values
  const getBackendLeaveType = () => {
    const leaveTypeMap = {
      earned: "EARN",
      casual: "CASUAL",
    };
    return leaveTypeMap[formData.leaveType] || "CASUAL";
  };

  // Map frontend duration to backend enum values
  const getBackendDuration = () => {
    switch (selectedLeaveType) {
      case "half":
        return "HALF_DAY";
      case "one":
        return "FULL_DAY";
      case "multiple":
        return "MULTI_DAY";
      default:
        return "FULL_DAY";
    }
  };

  // Map frontend half day session to backend enum values
  const getBackendHalfDaySession = () => {
    if (selectedHalfDay === "before") return "BEFORE_LUNCH";
    if (selectedHalfDay === "after") return "AFTER_LUNCH";
    return null;
  };

  // Function to get overlapping leave details
  const getOverlappingLeaveDetails = (newStart, newEnd) => {
    if (!existingLeavesData || existingLeavesData.length === 0) return null;

    const start = new Date(newStart);
    const end = new Date(newEnd);

    const overlappingLeave = existingLeavesData.find((leave) => {
      if (leave.status === "REJECTED" || leave.status === "Rejected") {
        return false;
      }

      const leaveStart = new Date(leave.startDate);
      const leaveEnd = new Date(leave.endDate);

      return start <= leaveEnd && end >= leaveStart;
    });

    return overlappingLeave;
  };

  // Check if date is weekend or holiday for single day/half day
  const isDateInvalidForSingleDay = (date) => {
    if (!date) return false;

    const dateObj = new Date(date);
    const day = dateObj.getDay();
    const isWeekend = day === 0 || day === 6;

    if (isWeekend) return true;

    // Check if it's a holiday
    const dateStr = dateObj.toISOString().split("T")[0];
    const isHoliday = holidays.some((holiday) => {
      if (!holiday.date) return false;
      let holidayDate = holiday.date;
      if (holidayDate.includes("T")) {
        holidayDate = holidayDate.split("T")[0];
      }
      return holidayDate === dateStr;
    });

    return isHoliday;
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.leaveType) {
      newErrors.leaveType = "Please select a leave type";
    } else {
      // Calculate requested days
      let totalRequestedDays = 0;

      if (selectedLeaveType === "multiple") {
        totalRequestedDays = calculateFormDays();
      } else if (selectedLeaveType === "half") {
        totalRequestedDays = 0.5;
      } else if (selectedLeaveType === "one") {
        totalRequestedDays = 1;
      }

      // Get the balance for selected leave type
      const balance =
        formData.leaveType === "earned"
          ? leaveBalances.earned
          : leaveBalances.casual;

      // Allow exact balance usage
      if (totalRequestedDays > balance.available) {
        newErrors.leaveType = `Insufficient ${formData.leaveType} leave balance. Available: ${balance.available} days, Requested: ${totalRequestedDays} days`;
      }
    }

    // Date validation for multiple days
    if (selectedLeaveType === "multiple") {
      if (!formData.startDate) {
        newErrors.startDate = "Please select a start date";
      }
      if (!formData.endDate) {
        newErrors.endDate = "Please select an end date";
      }
      if (formData.startDate && formData.endDate) {
        const start = new Date(formData.startDate);
        const end = new Date(formData.endDate);
        if (end < start) {
          newErrors.endDate = "End date cannot be before start date";
        }

        // Check for overlapping leaves with detailed message
        const overlappingLeave = getOverlappingLeaveDetails(
          formData.startDate,
          formData.endDate,
        );
        if (overlappingLeave) {
          const overlappingStart = new Date(
            overlappingLeave.startDate,
          ).toLocaleDateString();
          const overlappingEnd = new Date(
            overlappingLeave.endDate,
          ).toLocaleDateString();
          const overlappingStatus = overlappingLeave.status.toLowerCase();
          newErrors.startDate = `You already have a ${overlappingStatus} leave from ${overlappingStart} to ${overlappingEnd}`;
        }

        // Check if selected dates include only working days
        const totalDays = calculateFormDays();
        if (totalDays === 0) {
          newErrors.startDate =
            "Selected date range includes only weekends and holidays";
        }
      }
    } else {
      // For single day and half day - check if date is weekend or holiday
      if (!formData.startDate) {
        newErrors.startDate = "Please select a date";
      } else if (isDateInvalidForSingleDay(formData.startDate)) {
        newErrors.startDate = "Cannot apply leave on weekends or holidays";
      } else {
        // Check for overlapping leaves for single day/half day with detailed message
        const endDate = formData.startDate;
        const overlappingLeave = getOverlappingLeaveDetails(
          formData.startDate,
          endDate,
        );
        if (overlappingLeave) {
          const overlappingDate = new Date(
            overlappingLeave.startDate,
          ).toLocaleDateString();
          const overlappingStatus = overlappingLeave.status.toLowerCase();
          newErrors.startDate = `You already have a ${overlappingStatus} leave on ${overlappingDate}`;
        }
      }
    }

    // Half day validation
    if (selectedLeaveType === "half" && !selectedHalfDay) {
      newErrors.halfDay = "Please select before or after lunch";
    }

    // Reason validation
    if (!formData.reason.trim()) {
      newErrors.reason = "Please provide a reason for leave";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      const firstError = Object.values(errors)[0];
      if (firstError) showError(firstError);
      return;
    }

    if (!userId) {
      showError("User ID not found. Please make sure you're logged in.");
      return;
    }

    const startDate = formData.startDate;
    const endDate =
      selectedLeaveType === "multiple" ? formData.endDate : formData.startDate;

    // Final overlapping check before submission with detailed message
    const overlappingLeave = getOverlappingLeaveDetails(startDate, endDate);
    if (overlappingLeave) {
      const overlappingStart = new Date(
        overlappingLeave.startDate,
      ).toLocaleDateString();
      const overlappingEnd = new Date(
        overlappingLeave.endDate,
      ).toLocaleDateString();
      const overlappingStatus = overlappingLeave.status.toLowerCase();
      showError(
        `You already have a ${overlappingStatus} leave from ${overlappingStart} to ${overlappingEnd}. Please choose different dates.`,
      );
      return;
    }

    try {
      // Calculate total days
      let totalDays = 0;

      if (selectedLeaveType === "multiple") {
        totalDays = calculateFormDays();
      } else if (selectedLeaveType === "half") {
        totalDays = 0.5;
      } else if (selectedLeaveType === "one") {
        totalDays = 1;
      }

      // Prepare leave data
      const leaveData = {
        leaveType: getBackendLeaveType(),
        duration: getBackendDuration(),
        halfDaySession: getBackendHalfDaySession(),
        startDate: startDate,
        endDate: endDate,
        totalDays: totalDays,
        reason: formData.reason,
        applicantRole: "TECHNICIAN",
        divisionId: userInfo?.divisionId || "",
        departmentId: userInfo?.departmentId || "",
        academyId: academyId,
      };

      // Submit to API
      const result = await applyForLeave({ userId, leaveData }).unwrap();

      // Show success message
      toast.success("Leave application submitted successfully!");

      // Reset form and navigate
      setFormData({
        leaveType: "",
        startDate: "",
        endDate: "",
        reason: "",
      });
      setSelectedLeaveType("multiple");
      setSelectedHalfDay("");
      setSelectedStartDate(null);
      setSelectedEndDate(null);
      setErrors({}); // Clear all errors

      // Refetch data
      try {
        await refetchBalance();
        await refetchLeaves();
      } catch (refetchError) {}

      navigate("/manager/attendance/leave-records");
    } catch (error) {
      console.error("❌ Error in submission process:", error);

      let errorMessage =
        "Failed to submit leave application. Please try again.";

      if (error.status === 409) {
        errorMessage =
          "Leave application conflicts with existing leave. Please check your dates.";
      } else if (error.status === 400) {
        if (error.data) {
          if (typeof error.data === "string") {
            errorMessage = error.data;
          } else if (error.data.message) {
            errorMessage = error.data.message;
          }
        }
      } else if (error.data?.message) {
        errorMessage = error.data.message;
      }

      // Show specific error for insufficient quota
      if (
        errorMessage.includes("Insufficient") ||
        errorMessage.includes("quota")
      ) {
        const totalRequestedDays =
          selectedLeaveType === "multiple"
            ? calculateFormDays()
            : selectedLeaveType === "half"
              ? 0.5
              : 1;

        const available =
          formData.leaveType === "earned"
            ? leaveBalances.earned.available
            : leaveBalances.casual.available;

        errorMessage = `Insufficient ${formData.leaveType} leave balance. Available: ${available} days, Requested: ${totalRequestedDays} days`;
      }

      toast.error(errorMessage);
    }
  };

  const getLeaveTypeDisplay = () => {
    switch (formData.leaveType) {
      case "earned":
        return "Earned Leave";
      case "casual":
        return "Casual Leave";
      default:
        return "Select leave type";
    }
  };

  // Calculate min date for end date calendar (start date or today)
  const getEndDateMinDate = () => {
    if (formData.startDate) {
      return new Date(formData.startDate);
    }
    return new Date(); // Today
  };

  // Close calendar dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showStartCalendar && !event.target.closest(".start-date-calendar")) {
        setShowStartCalendar(false);
      }
      if (showEndCalendar && !event.target.closest(".end-date-calendar")) {
        setShowEndCalendar(false);
      }
      if (
        showLeaveTypeDropdown &&
        !event.target.closest(".leave-type-dropdown")
      ) {
        setShowLeaveTypeDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showStartCalendar, showEndCalendar, showLeaveTypeDropdown]);

  return (
    <div className="p-8 max-w-10xl mx-auto">
      {/* Error Overlay */}
      {/* {showErrorOverlay && (
        <ErrorOverlay message={errorMessage} onClose={hideError} />
      )} */}

      {/* Header */}
      <div className="flex items-center space-x-1 mb-6">
        <button
          aria-label="Back"
          onClick={handleBack}
          className=" md:flex w-9 h-9 items-center justify-center  rounded-full hover:bg-gray-100 hover:border-gray-400 transition-all"
        >
          <ChevronLeft size={24} strokeWidth={2} className="text-gray-700" />
        </button>
        <div className="flex items-center space-x-3">
          <h1 className="text-2xl font-bold text-gray-800">Apply Leave</h1>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Form */}
        <div className="lg:col-span-2">
          <div className="bg-[#FAFAFA] rounded-lg p-6">
            <h2 className="text-lg font-semibold text-gray-800 mb-4">
              Leave request form
            </h2>

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Select Leave Type with Icons */}
              <div>
                <h3 className="font-medium text-gray-700 mb-3">
                  Select a leave type
                </h3>
                <div className="grid grid-cols-3 gap-2 sm:gap-4">
                  {/* Half Day */}
                  <button
                    type="button"
                    onClick={() => handleLeaveTypeSelect("half")}
                    className={`py-3 px-2 sm:py-4 sm:px-4 border-2 rounded-lg text-center transition-colors flex flex-col items-center space-y-1 sm:space-y-2 justify-center ${
                      selectedLeaveType === "half"
                        ? "border-blue-500 bg-blue-50 text-blue-700"
                        : "border-gray-300 hover:border-gray-400"
                    }`}
                  >
                    <div className="w-8 h-8 sm:w-12 sm:h-12 flex items-center justify-center bg-gray-100 rounded-full">
                      <ClockFading className="w-5 h-5 sm:w-8 sm:h-8" />
                    </div>
                    <div className="font-medium text-xs sm:text-sm">
                      Half Day
                    </div>
                  </button>

                  {/* One Day */}
                  <button
                    type="button"
                    onClick={() => handleLeaveTypeSelect("one")}
                    className={`py-3 px-2 sm:py-4 sm:px-4 border-2 rounded-lg text-center transition-colors flex flex-col items-center space-y-1 sm:space-y-2 justify-center ${
                      selectedLeaveType === "one"
                        ? "border-blue-500 bg-blue-50 text-blue-700"
                        : "border-gray-300 hover:border-gray-400"
                    }`}
                  >
                    <div className="w-8 h-8 sm:w-12 sm:h-12 flex items-center justify-center bg-gray-100 rounded-full">
                      <Clock className="w-5 h-5 sm:w-8 sm:h-8" />
                    </div>
                    <div className="font-medium text-xs sm:text-sm">
                      One Day
                    </div>
                  </button>

                  {/* Multiple Days */}
                  <button
                    type="button"
                    onClick={() => handleLeaveTypeSelect("multiple")}
                    className={`py-3 px-2 sm:py-4 sm:px-4 border-2 rounded-lg text-center transition-colors flex flex-col items-center space-y-1 sm:space-y-2 justify-center ${
                      selectedLeaveType === "multiple"
                        ? "border-blue-500 bg-blue-50 text-blue-700"
                        : "border-gray-300 hover:border-gray-400"
                    }`}
                  >
                    <div className="w-8 h-8 sm:w-12 sm:h-12 flex items-center justify-center bg-gray-100 rounded-full">
                      <Hourglass className="w-5 h-5 sm:w-8 sm:h-8" />
                    </div>
                    <div className="font-medium text-xs sm:text-sm">
                      Multiple Days
                    </div>
                  </button>
                </div>
              </div>

              {/* Half Day Selection with Icons */}
              {selectedLeaveType === "half" && (
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="font-medium text-gray-700 mb-3 text-center">
                    Select Period
                  </h3>
                  <div className="flex justify-center space-x-4">
                    <div className="flex space-x-4 w-full max-w-md">
                      <button
                        type="button"
                        onClick={() => handleHalfDaySelect("before")}
                        className={`flex-1 py-4 px-4 border-2 rounded-lg text-center transition-colors flex flex-col items-center space-y-2 ${
                          selectedHalfDay === "before"
                            ? "border-blue-500 bg-blue-50 text-blue-700"
                            : "border-gray-300 hover:border-gray-400"
                        }`}
                      >
                        <Sun className="w-8 h-8" />
                        <div className="font-medium">Before Lunch</div>
                      </button>
                      <button
                        type="button"
                        onClick={() => handleHalfDaySelect("after")}
                        className={`flex-1 py-4 px-4 border-2 rounded-lg text-center transition-colors flex flex-col items-center space-y-2 ${
                          selectedHalfDay === "after"
                            ? "border-blue-500 bg-blue-50 text-blue-700"
                            : "border-gray-300 hover:border-gray-400"
                        }`}
                      >
                        <CloudSun className="w-8 h-8" />
                        <div className="font-medium">After Lunch</div>
                      </button>
                    </div>
                  </div>
                  {errors.halfDay && (
                    <p className="mt-2 text-sm text-red-600 text-center">
                      {errors.halfDay}
                    </p>
                  )}
                </div>
              )}

              {/* For Half Day and One Day - Flex layout */}
              {(selectedLeaveType === "half" ||
                selectedLeaveType === "one") && (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Leave Type Dropdown */}
                  <div className="leave-type-dropdown">
                    <h3 className="font-medium text-gray-700 mb-3">
                      Leave Type
                    </h3>
                    <div className="relative">
                      <button
                        type="button"
                        onClick={() =>
                          setShowLeaveTypeDropdown(!showLeaveTypeDropdown)
                        }
                        className={`w-full border-2 rounded-lg px-4 py-3 text-left flex items-center justify-between transition-colors ${
                          errors.leaveType
                            ? "border-red-500"
                            : formData.leaveType
                              ? "border-gray-300"
                              : "border-gray-300 hover:border-gray-400"
                        }`}
                      >
                        <div className="flex items-center space-x-3">
                          <span
                            className={
                              formData.leaveType
                                ? "text-blue-700 font-medium"
                                : "text-gray-500"
                            }
                          >
                            {getLeaveTypeDisplay()}
                          </span>
                        </div>
                        <ChevronDown
                          className={`w-5 h-5 transition-transform ${
                            showLeaveTypeDropdown ? "rotate-180" : ""
                          } ${formData.leaveType ? "text-blue-700" : "text-gray-400"}`}
                        />
                      </button>

                      {showLeaveTypeDropdown && (
                        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg">
                          <button
                            type="button"
                            onClick={() =>
                              handleLeaveTypeDropdownSelect("earned")
                            }
                            className="w-full px-4 py-3 text-left hover:bg-gray-50 first:rounded-t-lg last:rounded-b-lg border-b border-gray-100 last:border-b-0"
                          >
                            <div className="flex items-center space-x-3">
                              <RadioButton
                                selected={formData.leaveType === "earned"}
                              />
                              <div>
                                <span className="font-medium">
                                  Earned Leave
                                </span>
                                <div className="text-sm text-gray-500">
                                  Available: {leaveBalances.earned.available}{" "}
                                  days
                                  {leaveBalances.earned.pending > 0 &&
                                    ` (${leaveBalances.earned.pending} pending)`}
                                </div>
                              </div>
                            </div>
                          </button>
                          <button
                            type="button"
                            onClick={() =>
                              handleLeaveTypeDropdownSelect("casual")
                            }
                            className="w-full px-4 py-3 text-left hover:bg-gray-50 first:rounded-t-lg last:rounded-b-lg"
                          >
                            <div className="flex items-center space-x-3">
                              <RadioButton
                                selected={formData.leaveType === "casual"}
                              />
                              <div>
                                <span className="font-medium">
                                  Casual Leave
                                </span>
                                <div className="text-sm text-gray-500">
                                  Available: {leaveBalances.casual.available}{" "}
                                  days
                                  {leaveBalances.casual.pending > 0 &&
                                    ` (${leaveBalances.casual.pending} pending)`}
                                </div>
                              </div>
                            </div>
                          </button>
                        </div>
                      )}
                    </div>
                    {errors.leaveType && (
                      <p className="mt-2 text-sm text-red-600">
                        {errors.leaveType}
                      </p>
                    )}
                  </div>

                  {/* Date Selection with Calendar Dropdown */}
                  <div className="start-date-calendar relative">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Select date
                    </label>
                    <div className="relative">
                      <input
                        type="text"
                        readOnly
                        value={
                          formData.startDate
                            ? new Date(formData.startDate).toLocaleDateString()
                            : ""
                        }
                        placeholder="Select date"
                        className={`w-full border-2 rounded-lg px-4 py-3 bg-white cursor-pointer ${
                          errors.startDate
                            ? "border-red-500"
                            : "border-gray-300"
                        }`}
                        onClick={() => setShowStartCalendar(!showStartCalendar)}
                      />
                      <CalendarIconButton
                        onClick={() => setShowStartCalendar(!showStartCalendar)}
                        isOpen={showStartCalendar}
                      />

                      {/* Calendar Dropdown */}
                      {showStartCalendar && (
                        <CompactCalendarDropdown
                          isOpen={showStartCalendar}
                          onClose={() => setShowStartCalendar(false)}
                          selectedDate={selectedStartDate}
                          onDateSelect={handleStartDateSelect}
                          holidays={holidays}
                          disabledDates={disabledDates}
                          currentYear={currentYear}
                          position="bottom"
                        />
                      )}
                    </div>

                    {errors.startDate && (
                      <p className="mt-1 text-sm text-red-600">
                        {errors.startDate}
                      </p>
                    )}
                  </div>
                </div>
              )}

              {/* For Multiple Days - Compact layout */}
              {selectedLeaveType === "multiple" && (
                <>
                  {/* Leave Type Dropdown - Compact */}
                  <div className="leave-type-dropdown">
                    <h3 className="font-medium text-gray-700 mb-3">
                      Leave Type
                    </h3>
                    <div className="relative">
                      <button
                        type="button"
                        onClick={() =>
                          setShowLeaveTypeDropdown(!showLeaveTypeDropdown)
                        }
                        className={`w-full border-2 rounded-lg px-4 py-3 text-left flex items-center justify-between transition-colors ${
                          errors.leaveType
                            ? "border-red-500"
                            : formData.leaveType
                              ? "border-gray-300"
                              : "border-gray-300 hover:border-gray-400"
                        }`}
                      >
                        <div className="flex items-center space-x-3">
                          <span
                            className={
                              formData.leaveType
                                ? "text-blue-700 font-medium"
                                : "text-gray-500"
                            }
                          >
                            {getLeaveTypeDisplay()}
                          </span>
                        </div>
                        <ChevronDown
                          className={`w-5 h-5 transition-transform ${
                            showLeaveTypeDropdown ? "rotate-180" : ""
                          } ${formData.leaveType ? "text-blue-700" : "text-gray-400"}`}
                        />
                      </button>

                      {showLeaveTypeDropdown && (
                        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg">
                          <button
                            type="button"
                            onClick={() =>
                              handleLeaveTypeDropdownSelect("earned")
                            }
                            className="w-full px-4 py-3 text-left hover:bg-gray-50 first:rounded-t-lg last:rounded-b-lg border-b border-gray-100 last:border-b-0"
                          >
                            <div className="flex items-center space-x-3">
                              <RadioButton
                                selected={formData.leaveType === "earned"}
                              />
                              <div>
                                <span className="font-medium">
                                  Earned Leave
                                </span>
                                <div className="text-sm text-gray-500">
                                  Available: {leaveBalances.earned.available}{" "}
                                  days
                                  {leaveBalances.earned.pending > 0 &&
                                    ` (${leaveBalances.earned.pending} pending)`}
                                </div>
                              </div>
                            </div>
                          </button>
                          <button
                            type="button"
                            onClick={() =>
                              handleLeaveTypeDropdownSelect("casual")
                            }
                            className="w-full px-4 py-3 text-left hover:bg-gray-50 first:rounded-t-lg last:rounded-b-lg"
                          >
                            <div className="flex items-center space-x-3">
                              <RadioButton
                                selected={formData.leaveType === "casual"}
                              />
                              <div>
                                <span className="font-medium">
                                  Casual Leave
                                </span>
                                <div className="text-sm text-gray-500">
                                  Available: {leaveBalances.casual.available}{" "}
                                  days
                                  {leaveBalances.casual.pending > 0 &&
                                    ` (${leaveBalances.casual.pending} pending)`}
                                </div>
                              </div>
                            </div>
                          </button>
                        </div>
                      )}
                    </div>
                    {errors.leaveType && (
                      <p className="mt-2 text-sm text-red-600">
                        {errors.leaveType}
                      </p>
                    )}
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="start-date-calendar relative">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Start date
                      </label>
                      <div className="relative">
                        <input
                          type="text"
                          readOnly
                          value={
                            formData.startDate
                              ? new Date(
                                  formData.startDate,
                                ).toLocaleDateString()
                              : ""
                          }
                          placeholder="Select start date"
                          className={`w-full border-2 rounded-lg px-4 py-3 bg-white cursor-pointer ${
                            errors.startDate
                              ? "border-red-500"
                              : "border-gray-300"
                          }`}
                          onClick={() =>
                            setShowStartCalendar(!showStartCalendar)
                          }
                        />
                        <CalendarIconButton
                          onClick={() =>
                            setShowStartCalendar(!showStartCalendar)
                          }
                          isOpen={showStartCalendar}
                        />

                        {/* Calendar Dropdown */}
                        {showStartCalendar && (
                          <CompactCalendarDropdown
                            isOpen={showStartCalendar}
                            onClose={() => setShowStartCalendar(false)}
                            selectedDate={selectedStartDate}
                            onDateSelect={handleStartDateSelect}
                            holidays={holidays}
                            disabledDates={disabledDates}
                            currentYear={currentYear}
                            position="bottom"
                          />
                        )}
                      </div>
                      {errors.startDate && (
                        <p className="mt-1 text-sm text-red-600">
                          {errors.startDate}
                        </p>
                      )}
                    </div>

                    <div className="end-date-calendar relative">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        End date
                      </label>
                      <div className="relative">
                        <input
                          type="text"
                          readOnly
                          value={
                            formData.endDate
                              ? new Date(formData.endDate).toLocaleDateString()
                              : ""
                          }
                          placeholder="Select end date"
                          className={`w-full border-2 rounded-lg px-4 py-3 bg-white cursor-pointer ${
                            errors.endDate
                              ? "border-red-500"
                              : "border-gray-300"
                          }`}
                          onClick={() => setShowEndCalendar(!showEndCalendar)}
                        />
                        <CalendarIconButton
                          onClick={() => setShowEndCalendar(!showEndCalendar)}
                          isOpen={showEndCalendar}
                        />

                        {/* Calendar Dropdown */}
                        {showEndCalendar && (
                          <CompactCalendarDropdown
                            isOpen={showEndCalendar}
                            onClose={() => setShowEndCalendar(false)}
                            selectedDate={selectedEndDate}
                            onDateSelect={handleEndDateSelect}
                            holidays={holidays}
                            disabledDates={disabledDates}
                            currentYear={currentYear}
                            position="bottom"
                            minDate={getEndDateMinDate()} // Disable dates before start date
                          />
                        )}
                      </div>
                      {errors.endDate && (
                        <p className="mt-1 text-sm text-red-600">
                          {errors.endDate}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Total Working Days Display for Multiple Days */}
                  {selectedLeaveType === "multiple" &&
                    formData.startDate &&
                    formData.endDate && (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Total Leave Days (excluding weekends & holidays)
                          </label>
                          <div className="w-full border-2 border-gray-300 rounded-lg px-4 py-3 bg-gray-50">
                            {calculateFormDays()}{" "}
                            {calculateFormDays() === 1 ? "day" : "days"}
                          </div>
                        </div>
                      </div>
                    )}
                </>
              )}

              {/* Reason for Leave */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Reason for leave
                </label>
                <textarea
                  name="reason"
                  value={formData.reason}
                  onChange={handleInputChange}
                  rows={4}
                  placeholder="Please provide a detailed reason for leave request"
                  className={`w-full border-2 rounded-lg px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                    errors.reason ? "border-red-500" : "border-gray-300"
                  }`}
                />
                {errors.reason && (
                  <p className="mt-1 text-sm text-red-600">{errors.reason}</p>
                )}
              </div>

              {/* Submit Button */}
              <div className="flex justify-end pt-4">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-black text-white py-3 px-6 rounded-lg font-semibold hover:bg-zinc-800 transition-colors mb-4 flex items-center justify-center space-x-2 disabled:opacity-50"
                >
                  {isSubmitting ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                      <span>Submitting...</span>
                    </>
                  ) : (
                    <span>Submit</span>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>

        <div className="space-y-6">
          {/* Earned Leave Balance */}
          <LeaveBalanceCard
            title="Earned Leave Balance"
            balance={leaveBalances.earned}
            color="blue"
            isLoading={isLoadingBalance}
          />

          {/* Casual Leave Balance */}
          <LeaveBalanceCard
            title="Casual Leave Balance"
            balance={leaveBalances.casual}
            color="purple"
            isLoading={isLoadingBalance}
          />

          {/* Note */}
          <div className="bg-[#FAFAFA] border border-gray-200 rounded-lg p-4">
            <div className="flex items-start space-x-2 mb-2">
              <Lightbulb className="w-5 h-5 text-black-600 mt-0.1 ml-1 flex-shrink-0" />
              <h3 className="font-semibold text-gray-800">Note!</h3>
            </div>
            <ul className="text-sm text-gray-700 space-y-1 ml-2">
              <li className="flex items-start">
                <span className="text-gray-500 mr-2">•</span>
                You cannot apply for leave on weekends, holidays, or dates where
                you already have an approved or pending leave.
              </li>
              <li className="flex items-start">
                <span className="text-gray-500 mr-2">•</span>
                For multiple days, weekends and holidays are automatically
                excluded from the count.
              </li>
              <li className="flex items-start">
                <span className="text-gray-500 mr-2">•</span>
                Pending leaves are deducted from your available balance.
              </li>
              <li className="flex items-start">
                <span className="text-gray-500 mr-2">•</span>
                Only dates from {currentYear} are available for selection.
              </li>
              {isLoadingCalendars && (
                <li className="flex items-start">
                  <span className="text-gray-500 mr-2">•</span>
                  <span className="text-blue-600">
                    Loading holiday calendar...
                  </span>
                </li>
              )}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
